import { townColorHex as hex } from './townPropTemplates';
import type { TownPropPalette } from './townPropTemplates';

export interface PropPoint { readonly x: number; readonly y: number }
export interface PropBounds extends PropPoint { readonly width: number; readonly height: number }
export interface TimberBoardOptions { readonly axis?: 'horizontal' | 'vertical'; readonly boards?: number }
export interface MasonryBlockOptions { readonly courses?: number; readonly columns?: number }
export interface RopeSpanOptions { readonly sag?: number; readonly thickness?: number }

/** Bound authoring work; invalid counts return empty art rather than partial geometry. */
export const MAX_PROP_DIVISIONS = 64;
const divisions = (value: number) => Number.isSafeInteger(value) && value > 0 && value <= MAX_PROP_DIVISIONS;
const finitePoint = (point: PropPoint) => Number.isFinite(point.x) && Number.isFinite(point.y);
function usableBounds(b: PropBounds): boolean {
  return finitePoint(b) && Number.isFinite(b.width) && Number.isFinite(b.height) && b.width > 0 && b.height > 0
    && Number.isFinite(b.x + b.width) && Number.isFinite(b.y + b.height)
    && b.x + b.width > b.x && b.y + b.height > b.y;
}
function usablePalette(p: TownPropPalette): boolean {
  return ['primary', 'shade', 'wood', 'woodEdge', 'stone', 'iron'].every(key => {
    const color = p[key as keyof TownPropPalette];
    return Number.isInteger(color) && color >= 0 && color <= 0xffffff;
  });
}
const rect = (x: number, y: number, width: number, height: number, color: number) =>
  `<rect x="${x}" y="${y}" width="${width}" height="${height}" fill="${hex(color)}" />`;

/** Horizontal rows or vertical planks, with inset grain and internal seams.
 * Every fill and stroke stays inside bounds, including fractional dimensions. */
export function drawTimberBoards(b: PropBounds, palette: TownPropPalette, options: TimberBoardOptions = {}): string {
  const count = options.boards === undefined ? 3 : options.boards;
  const axis = options.axis === undefined ? 'horizontal' : options.axis;
  if (!usableBounds(b) || !usablePalette(palette) || !divisions(count)
    || (axis !== 'horizontal' && axis !== 'vertical')) return '';
  const horizontal = axis === 'horizontal', length = horizontal ? b.width : b.height;
  const breadth = (horizontal ? b.height : b.width) / count;
  if (!(breadth > 0)) return '';
  const line = Math.min(length, breadth) * 0.035;
  const point = (along: number, across: number) => horizontal
    ? `${b.x + along} ${b.y + across}` : `${b.x + across} ${b.y + along}`;
  const parts = ['<g data-prop-part="timber-boards">'];
  for (let i = 0; i < count; i++) {
    const start = i * breadth;
    parts.push(rect(horizontal ? b.x : b.x + start, horizontal ? b.y + start : b.y,
      horizontal ? b.width : breadth, horizontal ? breadth : b.height, i % 2 ? palette.wood : palette.primary));
    const grain = `M${point(length * .09, start + breadth * .35)} Q${point(length * .31, start + breadth * .22)} ${point(length * .68, start + breadth * .36)}
      M${point(length * .38, start + breadth * .7)} Q${point(length * .65, start + breadth * .58)} ${point(length * .9, start + breadth * .7)}`;
    parts.push(`<path d="${grain}" fill="none" stroke="${hex(palette.woodEdge)}" stroke-width="${line}" stroke-linecap="round" opacity="0.75" />`);
    if (i > 0) parts.push(`<path d="M${point(0, start)} L${point(length, start)}" fill="none" stroke="${hex(palette.shade)}" stroke-width="${line * 1.8}" />`);
  }
  parts.push('</g>');
  return parts.join('');
}

/** Alternating half-block courses. Edge blocks are intersected with bounds in
 * numbers, never clipped by an SVG ID; highlights are inset from those edges. */
export function drawMasonryBlocks(b: PropBounds, palette: TownPropPalette, options: MasonryBlockOptions = {}): string {
  const courses = options.courses === undefined ? 3 : options.courses;
  const columns = options.columns === undefined ? 3 : options.columns;
  if (!usableBounds(b) || !usablePalette(palette) || !divisions(courses) || !divisions(columns)) return '';
  const cellW = b.width / columns, cellH = b.height / courses;
  if (!(cellW > 0 && cellH > 0)) return '';
  const gap = Math.min(cellW, cellH) * .07, stroke = gap * .32;
  const parts = ['<g data-prop-part="masonry-blocks">', rect(b.x, b.y, b.width, b.height, palette.iron)];
  for (let row = 0; row < courses; row++) {
    for (let column = 0; column < columns + (row % 2); column++) {
      const rawX = b.x + (column - (row % 2) * .5) * cellW;
      const left = Math.max(b.x, rawX), right = Math.min(b.x + b.width, rawX + cellW);
      const x = left + gap / 2, y = b.y + row * cellH + gap / 2;
      const width = right - left - gap, height = cellH - gap;
      if (width <= 0 || height <= 0) continue;
      parts.push(rect(x, y, width, height, (row + column) % 3 === 1 ? palette.shade : palette.stone));
      const inset = Math.min(width, height) * .13;
      parts.push(`<path d="M${x + inset} ${y + height - inset} V${y + inset} H${x + width - inset}"
        fill="none" stroke="${hex(palette.woodEdge)}" stroke-width="${stroke}" opacity="0.65" />`);
    }
  }
  parts.push('</g>');
  return parts.join('');
}

/** A quadratic rope. Sag is the signed screen-Y offset at its midpoint,
 * so controlY = midpointY + 2*sag. Round stroke caps extend thickness/2
 * past endpoints; callers must reserve that radius around the curve.
 * The endpoint/control-point bounding box expanded by that radius is a safe
 * containment box. Coincident endpoints, invalid geometry or thickness return ''. */
export function drawRopeSpan(from: PropPoint, to: PropPoint, palette: TownPropPalette, options: RopeSpanOptions = {}): string {
  const sag = options.sag === undefined ? 8 : options.sag;
  const thickness = options.thickness === undefined ? 3 : options.thickness;
  if (!finitePoint(from) || !finitePoint(to) || !usablePalette(palette)
    || !Number.isFinite(sag) || !Number.isFinite(thickness) || thickness <= 0
    || (from.x === to.x && from.y === to.y)) return '';
  const cx = from.x / 2 + to.x / 2, cy = from.y / 2 + to.y / 2 + 2 * sag;
  const radius = thickness / 2;
  if (![cx, cy, from.x - radius, from.x + radius, to.x - radius, to.x + radius,
    from.y - radius, from.y + radius, to.y - radius, to.y + radius, cy - radius, cy + radius].every(Number.isFinite)) return '';
  const d = `M${from.x} ${from.y} Q${cx} ${cy} ${to.x} ${to.y}`;
  return `<g data-prop-part="rope-span" fill="none" stroke-linecap="round">
    <path d="${d}" stroke="${hex(palette.shade)}" stroke-width="${thickness}" />
    <path d="${d}" stroke="${hex(palette.woodEdge)}" stroke-width="${thickness * .68}" />
    <path d="${d}" stroke="${hex(palette.stone)}" stroke-width="${thickness * .24}" stroke-dasharray="${thickness * .4} ${thickness * 1.2}" />
  </g>`;
}
